/**
 * Claude Code Camp for Kids — Program Terms & Waiver, ONE source of truth.
 *
 * Rendered by BOTH surfaces:
 *   - /claude-code-for-kids/program-terms (canonical, linkable page)
 *   - the scrollable terms modal inside the checkout sheet
 *
 * Clickwrap posture (counsel-reviewed draft, 2026-07-21): enforceability
 * comes from conspicuous notice + the required unchecked checkbox at
 * checkout, NOT from forcing scroll-to-bottom. Keep the canonical URL —
 * receipts and emails link it. The version the buyer accepted is stamped
 * on the order via metadata.termsAccepted at checkout time.
 *
 * LAUNCH GATE: a licensed NY attorney should review before the event is
 * published (one attorney hour, per HANDOFF). Entity name confirmed by
 * Isaac 2026-07-21: AdVenture Media Digital Marketing LLC.
 */
import type { ReactNode } from "react";

export const KIDS_TERMS_EFFECTIVE_DATE = "July 23, 2026";

const ANTHROPIC_LINKS = {
  consumerTerms: "https://www.anthropic.com/legal/consumer-terms",
  usagePolicy: "https://www.anthropic.com/legal/aup",
  privacy: "https://www.anthropic.com/legal/privacy",
};

function Ext({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-indigo">
      {children}
    </a>
  );
}

const P = ({ children }: { children: ReactNode }) => (
  <p className="text-[14px] leading-relaxed text-charcoal/80 mb-3 max-w-[72ch]">{children}</p>
);

const CAPS = ({ children }: { children: ReactNode }) => (
  <p className="text-[13px] leading-relaxed text-charcoal/80 mb-3 max-w-[72ch] uppercase tracking-[0.01em]">
    {children}
  </p>
);

/** The full terms document body — headings + prose only, no page chrome. */
export function KidsProgramTermsBody() {
  const H = ({ children }: { children: ReactNode }) => (
    <h2 className="text-[16px] font-bold text-charcoal mt-8 mb-2.5 first:mt-0">{children}</h2>
  );
  return (
    <div data-testid="kids-terms-body">
      <p className="text-[13px] text-charcoal/55 mb-1">Effective date: {KIDS_TERMS_EFFECTIVE_DATE}</p>
      <p className="text-[13px] text-charcoal/55 mb-5">
        Provider: AdVenture Media Digital Marketing LLC, a New York limited liability company (&ldquo;AdVenture,&rdquo;
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;)
      </p>
      <P>
        These Program Terms &amp; Waiver (the &ldquo;Terms&rdquo;) are a binding agreement between AdVenture and the
        parent or legal guardian who enrolls a child in Claude Code Camp for Kids (the &ldquo;Program&rdquo;). Please
        read them in full — especially <strong>Section 5 (Refund Policy)</strong> and{" "}
        <strong>Section 7 (AI Acknowledgment)</strong>. By checking the agreement box at checkout and completing your
        purchase, you accept these Terms.
      </P>

      <H>1. Who may accept these Terms</H>
      <P>
        Enrollment is open only to a parent or legal guardian of the participating child (the &ldquo;Camper&rdquo;). By
        accepting these Terms, you represent and warrant that: (a) you are at least 18 years old; (b) you are the parent
        or legal guardian of each Camper you enroll; and (c) you have full legal authority to agree to these Terms on
        your own behalf and on behalf of each Camper. You are our sole point of contact for the Program. Campers cannot
        accept these Terms and do not contract with us.
      </P>

      <H>2. The Program</H>
      <P>
        The Program is a live, online, instructor-led coding camp for kids ages 10 to 16 in which Campers use AI tools —
        principally Claude and Claude Code, made by Anthropic, PBC (&ldquo;Anthropic&rdquo;) — to build software, under
        live instructor guidance and parent supervision. The camp schedule for the August 2026 cohort (all times
        Eastern): Parent Orientation on Monday, August 24, 2026; four live sessions — Tuesday, August 25; Wednesday,
        August 26; Thursday, August 27; and Monday, August 31, 2026 — each 6:30 to 8:00 PM; Family Demo Night on
        Wednesday, September 2, 2026; and post-camp build support through September 14, 2026 — all as published on the
        Program page. The Program is delivered over Zoom.
      </P>
      <P>
        The Program is an independent offering of AdVenture. It is not sponsored by, endorsed by, or affiliated with
        Anthropic.
      </P>

      <H>3. Anthropic accounts and the supervision requirement</H>
      <P>
        <strong>Anthropic&rsquo;s minimum age is 18.</strong> Anthropic&rsquo;s{" "}
        <Ext href={ANTHROPIC_LINKS.consumerTerms}>Consumer Terms of Service</Ext> require Claude account holders to be
        at least 18 years old (or older where local law requires). Campers therefore do not — and must not — hold
        Claude accounts of their own, and we never create accounts of any kind for Campers.
      </P>
      <P>
        <strong>Your account, your supervision.</strong> All Camper use of Claude happens on <strong>your</strong>{" "}
        Claude account, in accordance with the Program&rsquo;s supervised setup. You agree that: (a) you will remain the
        account holder at all times; (b) you will be present or within immediate reach during live sessions and will
        supervise your Camper&rsquo;s use of AI tools during and between sessions; and (c) your Camper&rsquo;s use of
        your account is your responsibility.
      </P>
      <P>
        <strong>Anthropic&rsquo;s terms govern your account.</strong> Your Claude account, and everything done on it, is
        governed by Anthropic&rsquo;s own agreements, not by these Terms: the{" "}
        <Ext href={ANTHROPIC_LINKS.consumerTerms}>Anthropic Consumer Terms of Service</Ext>, the{" "}
        <Ext href={ANTHROPIC_LINKS.usagePolicy}>Anthropic Usage Policy</Ext>, and the{" "}
        <Ext href={ANTHROPIC_LINKS.privacy}>Anthropic Privacy Policy</Ext>. You agree to comply with them, to ensure
        your Camper&rsquo;s supervised use complies with them, and to review Anthropic&rsquo;s minimum-age and privacy
        practices yourself. We do not control and are not responsible for Anthropic&rsquo;s services, policies, pricing,
        availability, or any action Anthropic takes on your account. A paid Claude subscription (recommended: Claude
        Pro) is purchased by you directly from Anthropic and is not part of Program tuition.
      </P>

      <H>4. Fees and pricing</H>
      <P>
        Tuition is the price displayed and paid at checkout, per Camper, including any early-bird pricing and
        per-child sibling rates shown at checkout. Sibling rates apply within a single order. Promotional and coupon
        codes do not apply to this Program unless we expressly state otherwise. All prices are in US dollars.
      </P>

      <H>5. Refund policy — Money-Back Satisfaction Guarantee</H>
      <CAPS>
        Please read this section carefully. It states the only circumstances in which refunds are available.
      </CAPS>
      <P>
        <strong>The Guarantee.</strong> You may claim a full, 100% refund of Program tuition at any time from purchase
        until <strong>one (1) hour after the scheduled end of the first live session</strong> of your Camper&rsquo;s
        cohort (the &ldquo;Guarantee Window&rdquo;). To claim it, contact us through our{" "}
        <Ext href="/contact">contact page</Ext> within the Guarantee Window; no reason is required. Refunds are issued
        to the original payment method.
      </P>
      <P>
        <strong>After the Guarantee Window.</strong> Once the Guarantee Window closes,{" "}
        <strong>all tuition is final and no refunds will be granted for any reason</strong>, except where a refund is
        required by applicable law or expressly provided below. This includes, without limitation, scheduling
        conflicts, technology problems on your side, loss of interest, or withdrawal from the Program.
      </P>
      <P>
        <strong>Missed sessions.</strong> Missing one or more sessions — for any reason — does not entitle you to a
        refund, credit, or price reduction. Session recordings are provided to enrolled families so a missed session
        never means missed material.
      </P>
      <P>
        <strong>Our scheduling rights.</strong> We may reschedule individual sessions, adjust session times, or
        substitute instructors of comparable qualification when reasonably necessary. We will give you as much notice
        as practicable. These changes do not entitle you to a refund, provided the Program as a whole is still
        delivered.
      </P>
      <P>
        <strong>If we cancel.</strong> If we cancel the Program outright (not merely reschedule it), you will receive a
        full refund of all tuition paid. That refund is your sole and exclusive remedy for our cancellation.
      </P>

      <H>6. Assumption of risk</H>
      <P>
        Participation in any live online program involves inherent risks that cannot be eliminated, including exposure
        to third-party technology failures, interruptions, and the AI-related risks described in Section 7. You
        voluntarily enroll your Camper with knowledge of these risks and, to the fullest extent permitted by law, you
        assume them on your own behalf and on behalf of your Camper.
      </P>

      <H>7. AI acknowledgment — please read</H>
      <P>
        The Program is built around generative artificial intelligence. You acknowledge and agree to the following:
      </P>
      <P>
        <strong>AI output is probabilistic.</strong> Generative AI systems such as Claude produce output by statistical
        prediction, not by retrieval of verified facts. Their output is inherently probabilistic and cannot be fully
        predicted, scripted, or controlled — by us, by you, or by Anthropic.
      </P>
      <P>
        <strong>No guarantee of output content.</strong> We employ safety measures, including live instructor
        supervision, a curriculum designed for supervised use, age-context prompt setup, and the parent-supervision
        requirement in Section 3. Despite these measures,{" "}
        <strong>
          we cannot and do not guarantee that AI output your Camper encounters will be free of inaccurate, misleading,
          inappropriate, offensive, or upsetting content.
        </strong>{" "}
        Teaching Campers to critically evaluate AI output is itself part of the curriculum, which necessarily involves
        encountering imperfect output.
      </P>
      <P>
        <strong>Acceptance of risk.</strong> You acknowledge that you understand the nature of generative AI as
        described in this Section, you accept the risk that your Camper may encounter such content notwithstanding our
        safety measures, and you agree that your supervision under Section 3 is a material part of the Program&rsquo;s
        safety design. This Section does not limit any right you may have under applicable law that cannot be waived.
      </P>

      <H>8. Session recordings</H>
      <P>
        Every session is recorded so enrolled families can review material and catch up on missed sessions. Recordings
        capture the instructor&rsquo;s screen, session audio, and the video feeds of participants whose cameras are on.{" "}
        <strong>Camera use is optional for Campers and is your choice as the parent.</strong> A Camper whose camera is
        on may appear in the recording, and Camper voices may be audible when they speak. If you prefer that your
        Camper not appear on video, keep their camera off; Campers can participate fully with the camera off. Campers
        are identified on screen by first name and last initial only.
      </P>
      <P>
        <strong>Consent.</strong> By enrolling, you consent to this recording practice on behalf of yourself and your
        Camper, including the capture of your Camper&rsquo;s voice when they speak and of their image whenever you
        allow their camera to be on. This consent covers recording, storage, and distribution of recordings to
        enrolled families as described in this Section. It does not extend to marketing or public use of any
        recording: recordings are never used in marketing, and marketing use of Camper work is governed exclusively by
        the separate, optional consent in Section 9.
      </P>
      <P>
        Recordings are licensed to enrolled families for personal, household use only and may not be shared, posted,
        or distributed outside your household. We retain recordings for as long as needed to provide Program access to
        enrolled families and then delete them in the ordinary course; we do not retain them indefinitely.
      </P>

      <H>9. Media and showcase consent (optional — separate opt-in)</H>
      <P>
        At checkout you may separately opt in to allow us to share what your Camper builds — project screenshots,
        demos, and descriptions — in our marketing and on our website. If you opt in: attribution is limited to your
        Camper&rsquo;s <strong>first name and last initial only</strong> (and, where relevant, age); we will never
        publish photos or video of your Camper under this consent; and this consent is <strong>not</strong> a condition
        of enrollment. You may withdraw it at any time through our <Ext href="/contact">contact page</Ext>, and we will
        stop new uses promptly (previously distributed materials may persist where recall is impracticable). If you do
        not opt in, we will not use your Camper&rsquo;s work in marketing.
      </P>

      <H>10. Children&rsquo;s privacy</H>
      <P>
        We practice data minimization. The information we collect about your Camper is their{" "}
        <strong>name and age</strong>, provided by you at checkout and used for the roster, Demo Night, and the
        diploma, plus their voice and, if you allow their camera on, their image as part of the session recordings
        described in Section 8.
        We do not create accounts for children, do not collect children&rsquo;s email addresses or contact information,
        do not communicate with Campers directly outside live sessions, and do not sell children&rsquo;s personal
        information or use it for advertising. All Program communication goes to the parent&rsquo;s email. Data handled
        by third-party services (Zoom, Anthropic, our payment processor) is governed by those services&rsquo; own
        privacy policies, including the <Ext href={ANTHROPIC_LINKS.privacy}>Anthropic Privacy Policy</Ext>. Our general{" "}
        <Ext href="/privacy">Privacy Policy</Ext> covers everything else.
      </P>

      <H>11. Code of conduct; removal</H>
      <P>
        At least two adults from our team staff every session, always including an instructor. Private direct messages
        between instructors and Campers are never used; all communication happens in the visible session or through the
        parent&rsquo;s email. Campers are expected to behave respectfully toward instructors and other Campers, follow
        instructor direction, and use AI tools only as directed within the Program. Prohibited conduct includes
        harassment, disruption of sessions, attempts to elicit inappropriate AI output, and sharing of session links or
        recordings outside the household. <strong>
          We may, in our reasonable discretion, remove any Camper whose behavior materially disrupts the Program,
          without refund
        </strong>{" "}
        (except where a refund is required by applicable law). Where practical, we will raise concerns with you before
        removal.
      </P>

      <H>12. Technology requirements; third-party services</H>
      <P>
        You are responsible for providing a suitable computer, reliable internet, Zoom access, and your Claude account
        (Section 3). We walk through the full setup together at Parent Orientation. The Program depends on third-party
        services — including Zoom and Anthropic — that we do not control. Each is governed by its own terms and privacy
        policy, and we are not liable for their outages, changes, or acts. If a technical failure{" "}
        <strong>on our side</strong> prevents a session from running, we will reschedule it or provide the recording
        plus additional support; that is your sole remedy for such a failure.
      </P>

      <H>13. Intellectual property</H>
      <P>
        <strong>Your Camper&rsquo;s work is your Camper&rsquo;s.</strong> As between you and us, your family owns the
        projects, code, and applications your Camper creates in the Program (subject to any rights of Anthropic or
        other tool providers under their terms, and to the limited license below). If you opt in under Section 9, you
        grant us a non-exclusive, royalty-free license to display and reproduce the Camper&rsquo;s projects for the
        purposes described there.
      </P>
      <P>
        <strong>Our curriculum is ours.</strong> We own all rights in the Program itself — curriculum, session
        materials, the Camp Field Guide, recordings, prompts, teaching methods, and branding. We grant your household a
        personal, non-transferable, non-commercial license to use the materials and recordings you receive. You may not
        resell, republish, or use them to operate a competing program.
      </P>

      <H>14. No guarantee of outcomes; educational disclaimer</H>
      <P>
        The Program is an educational experience. We do not guarantee any particular learning outcome, skill level,
        project result, academic improvement, or future benefit for any Camper. Testimonials and examples describe
        individual experiences and are not promises of results.
      </P>

      <H>15. Disclaimer of warranties</H>
      <CAPS>
        Except as expressly stated in these Terms, the Program and all materials are provided &ldquo;as is&rdquo; and
        &ldquo;as available,&rdquo; without warranties of any kind, express or implied, including warranties of
        merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Program
        will be uninterrupted or error-free, or that AI output will be accurate or appropriate (see Section 7). Some
        jurisdictions do not allow certain warranty disclaimers, so some of the above may not apply to you.
      </CAPS>

      <H>16. Limitation of liability</H>
      <CAPS>
        To the fullest extent permitted by law: (a) our total aggregate liability to you and your Camper(s) arising out
        of or relating to the Program or these Terms, regardless of the theory of liability, shall not exceed the total
        fees you paid us for the Program; and (b) we shall not be liable for any indirect, incidental, consequential,
        special, exemplary, or punitive damages, or for lost profits or lost data, even if advised of their
        possibility. Nothing in these Terms limits liability that cannot be limited under applicable law, including
        liability for gross negligence, willful misconduct, or any non-waivable statutory right.
      </CAPS>

      <H>17. Indemnification</H>
      <P>
        You agree to indemnify, defend, and hold harmless AdVenture and its officers, employees, instructors, and
        agents from and against claims, damages, and reasonable expenses (including attorneys&rsquo; fees) arising out
        of: (a) your breach of these Terms, including the supervision and account obligations in Section 3; (b) your or
        your Camper&rsquo;s violation of Anthropic&rsquo;s or Zoom&rsquo;s terms; or (c) your Camper&rsquo;s
        misconduct. This obligation does not apply to the extent a claim arises from our own gross negligence or
        willful misconduct.
      </P>

      <H>18. Governing law; disputes</H>
      <P>
        These Terms are governed by the laws of the State of New York, without regard to its conflict-of-laws rules.
        Any dispute arising out of or relating to these Terms or the Program shall be brought exclusively in the state
        or federal courts located in New York, and you consent to their jurisdiction. Nothing in this Section prevents
        either party from pursuing a claim in small-claims court where eligible.
      </P>

      <H>19. Severability; no waiver</H>
      <P>
        If any provision of these Terms is held unenforceable, it will be modified to the minimum extent necessary to
        make it enforceable, and the remaining provisions will remain in full force. Our failure to enforce any
        provision is not a waiver of it.
      </P>

      <H>20. Changes to these Terms</H>
      <P>
        We may update these Terms from time to time. The version you accepted at checkout governs your enrollment for
        the cohort you purchased; material changes will not be applied retroactively to an active enrollment without
        your consent. The current version, with its effective date, is always available at this page.
      </P>

      <H>21. Entire agreement; contact</H>
      <P>
        These Terms, together with the checkout page describing what you purchased and our general Privacy Policy, are
        the entire agreement between you and us regarding the Program, and supersede any prior discussions. Questions?{" "}
        <Ext href="/contact">Contact us</Ext>, or raise them at Parent Orientation before camp begins.
      </P>

      <p className="text-[13px] leading-relaxed text-charcoal/60 mt-6 max-w-[72ch] border-t border-charcoal/10 pt-4">
        By checking the agreement box at checkout, you confirm you have read, understood, and agree to these Program
        Terms &amp; Waiver on your own behalf and on behalf of each enrolled Camper.
      </p>
    </div>
  );
}
